import { PublicClientApplication, Configuration } from '@azure/msal-browser';

const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_ENTRA_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_ENTRA_TENANT_ID}`,
    redirectUri: window.location.origin + '/auth/callback',
  },
  cache: { cacheLocation: 'sessionStorage' },
};

export const msalInstance = new PublicClientApplication(msalConfig);

// MSAL.js v3+ requires explicit initialize() before any other API call.
// Cache the promise so concurrent callers all wait on the same init.
let initPromise: Promise<void> | null = null;
export function ensureMsalReady(): Promise<void> {
  if (!initPromise) {
    initPromise = msalInstance.initialize();
  }
  return initPromise;
}

export const loginRequest = {
  scopes: ['openid', 'profile', 'email', `api://${import.meta.env.VITE_ENTRA_CLIENT_ID}/Reports.Read`],
};

export async function getAccessToken(): Promise<string> {
  await ensureMsalReady();
  const accounts = msalInstance.getAllAccounts();
  if (!accounts.length) throw new Error('Not signed in');
  // Silent acquisition only — never auto-redirect from inside this helper.
  // An auto-redirect causes a sign-in loop when the API rejects the token
  // for any reason (audience mismatch, scope mismatch, expired refresh).
  // The caller decides whether to surface an error or prompt re-login.
  const result = await msalInstance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
  return result.accessToken;
}

export async function login(): Promise<void> {
  await ensureMsalReady();
  // Clear any stale accounts so loginRedirect can't short-circuit silently.
  // We do NOT use logoutRedirect here — that would round-trip through Microsoft
  // and back unnecessarily; we just drop the local cache.
  const accounts = msalInstance.getAllAccounts();
  for (const acct of accounts) {
    try { msalInstance.setActiveAccount(null); } catch { /* noop */ }
    try { (msalInstance as any).getTokenCache?.()?.removeAccount?.(acct); } catch { /* noop */ }
  }
  await msalInstance.loginRedirect({
    ...loginRequest,
    prompt: 'select_account',  // always show the account picker
  });
}

export async function logout(): Promise<void> {
  await ensureMsalReady();
  await msalInstance.logoutRedirect();
}
